import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Plan } from '../../models/subscription.model';
import { AuthService } from '../../services/auth.service';
import { PaymentService } from '../../services/payment.service';
import { PublicDataService } from '../../services/public-data.service';
import { CouponService } from '../../services/coupon.service';

@Component({
  selector: 'app-plans-page',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<section class="min-h-[calc(100vh-120px)] bg-slate-950 py-12 text-slate-100">
  <div class="container mx-auto px-4">
    <div class="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-300/90">Assinatura</p>
        <h1 class="mt-2 text-3xl font-bold tracking-tight text-white md:text-4xl">Planos de evolução</h1>
        <p class="mt-1.5 text-sm text-slate-400">Escolha o plano ideal para seu ritmo de estudo.</p>
      </div>
      @if (isAuthenticated()) {
        <button
          (click)="onBackToApp()"
          class="rounded-xl border border-slate-700 bg-slate-900/70 px-5 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white">
          Voltar ao app
        </button>
      }
    </div>

    @if (isLoadingPlans()) {
      <div class="text-center">
        <div class="loader mx-auto"></div>
        <p class="mt-4 text-slate-400">Carregando planos...</p>
      </div>
    } @else if (plans().length === 0) {
      <div class="mx-auto max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-8 text-center">
        <p class="text-lg font-semibold text-white">Não foi possível carregar os planos no momento.</p>
        <button (click)="reloadPlans()" class="mt-6 rounded-lg bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-500">
          Tentar novamente
        </button>
      </div>
    } @else {
      <div class="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-4">
        @for (plan of planFeatures(); track plan.id) {
          <article
            class="relative flex h-full flex-col rounded-2xl border p-6 transition-shadow hover:shadow-lg"
            [ngClass]="isMostPopular(plan)
              ? 'border-blue-400/60 bg-slate-900 shadow-xl shadow-blue-900/20 ring-1 ring-blue-400/20'
              : 'border-slate-700/60 bg-slate-950/60 hover:border-slate-600'">

            @if (isMostPopular(plan)) {
              <span class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                Recomendado
              </span>
            }

            <div class="flex-grow">
              <h2 class="text-xl font-bold tracking-tight text-white">{{ plan.name }}</h2>
              <p class="mt-2 text-sm text-slate-400">{{ plan.description }}</p>

              <div class="my-6">
                @if (plan.price === 0) {
                  <span class="text-4xl font-black text-white">Grátis</span>
                } @else {
                  <span class="text-4xl font-black text-white">R$ {{ (plan.price / 100).toFixed(2).replace('.', ',') }}</span>
                  <span class="text-sm text-slate-400">{{ getPlanIntervalLabel(plan) }}</span>
                }
              </div>

              <ul class="space-y-2 text-sm text-slate-300">
                @for (feature of plan.features; track feature) {
                  <li class="flex items-start">
                    <svg class="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.86-9.81a.75.75 0 0 0-1.21-.88l-3.49 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" />
                    </svg>
                    <span>{{ feature }}</span>
                  </li>
                }
              </ul>
            </div>

            <button
              (click)="onSelectPlan(plan)"
              class="mt-8 w-full rounded-xl px-4 py-3 font-bold transition"
              [class.bg-blue-600]="plan.price > 0"
              [class.text-white]="plan.price > 0"
              [class.hover:bg-blue-500]="plan.price > 0"
              [class.bg-slate-100]="plan.price === 0"
              [class.text-slate-900]="plan.price === 0"
              [class.hover:bg-white]="plan.price === 0">
              @if (plan.price === 0) { Usar plano grátis } @else { Assinar agora }
            </button>
          </article>
        }
      </div>
    }
  </div>
</section>
`,
})
export class PlansPageComponent implements OnInit, OnDestroy {
  requestBackToApp = output<void>();
  requestLogin = output<void>();

  private publicDataService = inject(PublicDataService);
  private authService = inject(AuthService);
  private paymentService = inject(PaymentService);
  private couponService = inject(CouponService);

  readonly plans = signal<Plan[]>([]);
  readonly isLoadingPlans = signal(true);
  readonly isAuthenticated = this.authService.isAuthenticated;
  readonly activeCouponCode = this.couponService.activeCouponCode;
  private stopPlansWatch?: () => void;

  readonly planFeatures = computed(() => {
    return this.plans().map((plan) => {
      const planName = this.normalizePlanName(plan.name);
      let features: string[] = [];

      if (planName.includes('gratuito')) {
        features = ['1 correção por mês', 'Análise básica', 'Análise por banca'];
      } else if (planName.includes('basico')) {
        features = [`${plan.credits_granted} correções por mês`, 'Análise padrão', 'Histórico das 10 últimas redações'];
      } else if (planName.includes('intermediario')) {
        features = [`${plan.credits_granted} correções por mês`, 'Análise avançada', 'Histórico ilimitado'];
      } else if (planName.includes('pro')) {
        features = ['Correções ilimitadas', 'Análise completa', 'Plano de evolução com IA'];
      }

      if (features.length === 0) {
        features = plan.price === 0
          ? ['Plano inicial para conhecer a plataforma', 'Análise essencial para iniciar']
          : [`${plan.credits_granted} correções por ciclo`, 'Feedback técnico por critério', 'Histórico de desempenho'];
      }

      return { ...plan, features };
    });
  });

  ngOnInit(): void {
    void this.reloadPlans();
    this.stopPlansWatch = this.publicDataService.watchPlansUpdates(() => {
      void this.reloadPlans(true);
    });
  }

  ngOnDestroy(): void {
    this.stopPlansWatch?.();
  }

  private normalizePlanName(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }

  getPlanIntervalLabel(plan: Plan): string {
    switch (plan.interval) {
      case 'month':
        return '/mês';
      case 'quarter':
        return '/trimestre';
      case 'year':
        return '/ano';
      default:
        return '';
    }
  }

  async reloadPlans(silent = false) {
    this.isLoadingPlans.set(true);
    try {
      const plansData = await this.publicDataService.getPlans();
      const filteredPlans = plansData.filter((p) => p.interval !== 'credits');
      this.plans.set(filteredPlans.sort((a, b) => a.price - b.price));
    } catch {
      if (!silent) {
        this.plans.set([]);
      }
    } finally {
      this.isLoadingPlans.set(false);
    }
  }

  onBackToApp() {
    this.requestBackToApp.emit();
  }

  async onSelectPlan(plan: Plan) {
    if (this.isAuthenticated()) {
      if (plan.price > 0) {
        await this.paymentService.startCheckout(String(plan.id), { preopen: true, couponCode: this.activeCouponCode() });
      } else {
      this.authService.showNotification('info', 'Plano gratuito', 'Sua conta já possui os benefícios do plano gratuito.');
      }
      return;
    }

    if (plan.price > 0) {
      this.authService.setPendingPriceId(String(plan.id));
      this.authService.showNotification('info', 'Faça login para continuar', 'Entre na sua conta para prosseguir com a assinatura.');
      this.requestLogin.emit();
      return;
    }

    this.requestLogin.emit();
  }

  isMostPopular(plan: Plan): boolean {
    const name = this.normalizePlanName(plan.name);
    return name.includes('intermediario');
  }

}
